import loglevel from "loglevel";

import type { PlayedSongRequestData } from "services/api";
import DEBUG from "util/debugSettings";

const logger = loglevel.getLogger("player.stats");

type SkippedRanges = PlayedSongRequestData["skippedRanges"];

export default class PlayedSong {
  secondsListened = 0;
  accumulateFrom = 0;
  duration = 0;
  position = 0;
  readonly startedAt = new Date();
  readonly skippedRanges: SkippedRanges = [];
  private positionInitialized = false;

  constructor(readonly songId: string) {
    if (DEBUG.SAFARI_PLAYER_LOGS) {
      logger.debug(
        `PlayedSong created for song ${songId} - initializing tracking`
      );
    }
  }

  updatePosition(position: number, duration: number) {
    // Initialize accumulateFrom on first position update
    if (!this.positionInitialized && position > 0) {
      this.accumulateFrom = position;
      this.positionInitialized = true;
      if (DEBUG.SAFARI_PLAYER_LOGS) {
        logger.debug(
          `Song ${
            this.songId
          } tracking initialized at position ${position.toFixed(2)}`
        );
      }
    }

    // Only log if position has changed significantly (more than 1 second)
    if (DEBUG.SAFARI_PLAYER_LOGS && Math.abs(this.position - position) > 1) {
      logger.debug(
        `Song ${this.songId} position updated: ${this.position.toFixed(
          2
        )} → ${position.toFixed(
          2
        )}, accumulateFrom: ${this.accumulateFrom.toFixed(
          2
        )}, secondsListened: ${this.secondsListened.toFixed(2)}`
      );
    }
    this.position = position;
    this.duration = duration;
  }

  updateSkippedRanges(range: SkippedRanges[0]) {
    const addedTime = this.position - this.accumulateFrom;
    if (addedTime > 0) {
      this.secondsListened += addedTime;
      if (DEBUG.SAFARI_PLAYER_LOGS) {
        logger.debug(
          `Song ${this.songId} skip detected - Added ${addedTime.toFixed(
            2
          )}s to total (from ${this.accumulateFrom.toFixed(
            2
          )} to ${this.position.toFixed(2)})`
        );
      }
    } else if (DEBUG.SAFARI_PLAYER_LOGS) {
      logger.debug(
        `Song ${
          this.songId
        } skip detected - No time added (from ${this.accumulateFrom.toFixed(
          2
        )} to ${this.position.toFixed(2)})`
      );
    }
    this.accumulateFrom = range.to;
    this.skippedRanges.push(range);
    if (DEBUG.SAFARI_PLAYER_LOGS) {
      logger.debug(
        `New accumulateFrom: ${this.accumulateFrom.toFixed(
          2
        )}, total secondsListened: ${this.secondsListened.toFixed(2)}`
      );
    }
  }

  async songFinished(songPlayed: (stats: PlayedSongRequestData) => void) {
    const finalSegment = this.position - this.accumulateFrom;

    if (finalSegment > 0) {
      this.secondsListened += finalSegment;
      if (DEBUG.SAFARI_PLAYER_LOGS) {
        logger.debug(
          `Song ${
            this.songId
          } finished - Added final segment of ${finalSegment.toFixed(
            2
          )}s (from ${this.accumulateFrom.toFixed(
            2
          )} to ${this.position.toFixed(2)})`
        );
      }
    } else if (DEBUG.SAFARI_PLAYER_LOGS) {
      logger.debug(
        `Song ${
          this.songId
        } finished - No final segment added (from ${this.accumulateFrom.toFixed(
          2
        )} to ${this.position.toFixed(2)})`
      );
    }

    const { songId, duration, secondsListened, startedAt, skippedRanges } =
      this;

    // IMPORTANT: Always log the seconds listened - this is a critical metric
    // that should be visible to all developers regardless of debug settings
    logger.info(
      `Song ${songId} finished - Seconds listened: ${secondsListened.toFixed(
        2
      )}. Duration: ${duration.toFixed(2)}`
    );

    if (secondsListened > 0 && duration > 0) {
      const finishedAt = new Date().toISOString();
      const data: PlayedSongRequestData = {
        songId,
        duration,
        secondsListened,
        startedAt: startedAt.toISOString(),
        finishedAt,
        skippedRanges,
      };
      songPlayed(data);
    } else {
      // Always show warning when no play time recorded - this indicates a potential issue
      logger.warn(
        `Song ${songId} had no play time recorded (${secondsListened.toFixed(
          2
        )}s). Data not saved to database.`
      );
    }
  }
}
